import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// Store
import { rootReducer } from './store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { AuthenticationEffects } from './store/Authentication/authentication.effects';

import { environment } from '../environments/environment';

import { ErrorInterceptor } from './core/helpers/error.interceptor';
import { JwtInterceptor } from './core/helpers/jwt.interceptor';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { LayoutsModule } from "./layouts/layouts.module";
import { PagesModule } from "./pages/pages.module";
import { ClientEffects } from './store/Client/client.effects';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AngularFireModule } from '@angular/fire/compat';
import { TeamMemberEffects } from './store/TeamMember/teammember.effects';
import { PlansetEffects } from './store/Planset/planset.effects';
import { ServiceChargesEffects } from './store/ServiceCharges/servicecharges.effects';
import { DataentryEffects } from './store/Dataentry/dataentry.effects';
import { CommonEffects } from './store/common/common.effects';
import { SubscriptionEffects } from './store/subscription/subscription.effects';
import { SalesproposalEffects } from './store/Salesproposal/salesproposal.effects';
import { PestampEffects } from './store/Pestamp/pestamp.effects';
import { TicketEffects } from './store/Ticket/ticket_effect';
import { DashboardEffects } from './store/Dashboard/dashboard.effects';
import { GoogleApiService } from './core/services/googleapiservice';
import { ArchiveEffects } from './store/Archive/archive.effects';
import { StampingpartnerEffects } from './store/StampingPartner/stampingpartner.effects';
import { AutomationEffects } from './store/Automation/automation_effect';
import { PtoEffects } from './store/Pto/pto.effects';
import { PermittingEffects } from './store/Permitting/permitting.effects';
import { DialogflowEffects } from './store/Dilogflow/dilogflow.effects';
import { ZaiautogenPlansetEffects } from './store/zaiautogenplanset/zaiautogenplanset.effects';
import { SurveyEffects } from './store/Survey/survey.effects';
import { PlacardEffects } from './store/Placards/placard.effects';
import { LegendEffects } from './store/Legends/legend.effects';
import { IncentiveEffects } from './store/Incentive/incentive.effects';

export function clearState(reducer: any) {
    return function (state: any, action: any) {
      
        if (action.type === '[Authentication] Logout') {
          // Create a new state object with the layout property
          state = { layout: state.layout };
        }
      
        return reducer(state, action);
    };
}

export function initializeApp(googleApiService: GoogleApiService) {
    return (): Promise<void> => googleApiService.loadApi();
}

@NgModule({
    declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserAnimationsModule,
        BrowserModule,
        AppRoutingModule,
        LayoutsModule,
        PagesModule,
        StoreModule.forRoot(rootReducer, { metaReducers: [clearState] }),
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: environment.production, // Restrict extension to log-only mode
        }),
        EffectsModule.forRoot([
            AuthenticationEffects,
            ClientEffects,
            TeamMemberEffects,
            PlansetEffects,
            PtoEffects,
            PermittingEffects,
            SalesproposalEffects,
            PestampEffects,
            ServiceChargesEffects,
            DataentryEffects,
            CommonEffects,
            SubscriptionEffects,
            TicketEffects,
            DashboardEffects,
            ArchiveEffects,
            StampingpartnerEffects,
            DialogflowEffects,
            ZaiautogenPlansetEffects,
            AutomationEffects,
            SurveyEffects,
            PlacardEffects,
            LegendEffects,
            IncentiveEffects
        ]),
        AngularFireModule.initializeApp(environment.firebaseConfig),
        AngularFirestoreModule], providers: [
            {
                provide: ErrorHandler,
                useValue: Sentry.createErrorHandler({
                    showDialog: false,
                }),
            }, {
                provide: Sentry.TraceService,
                deps: [Router],
            },
            {
                provide: APP_INITIALIZER,
                useFactory: () => () => { },
                deps: [Sentry.TraceService],
                multi: true,
            },
            GoogleApiService,
            {
                provide: APP_INITIALIZER,
                useFactory: initializeApp,
                deps: [GoogleApiService],
                multi: true
            },
            { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
            { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
            provideHttpClient(withInterceptorsFromDi()),
        ]
})
export class AppModule { }
